import filter from 'lodash/filter'
import join from 'lodash/join'
import map from 'lodash/map'
import * as React from 'react'
import {maybe, fromMaybe} from '@freckle/maybe'

// Types
import {
  type StudentWithSisMatchesT,
  type DistrictStudentT,
  type TeacherAttrT
} from '@frontrow/console/src/models/student-sis-ids'
import {type PatchStudentSisIdT} from '@frontrow/console/src/components/pages/import-sis-ids'
import SisMatchesDisplay from './sis-matches-display'

const MatchCategories = {
  name: 'MatchesName',
  teacher: 'MatchesTeacher',
  grade: 'MatchesGrade'
}

type MatchCheckboxT = 'all-match' | 'name-teacher-match' | 'name-grade-match'

export type SelectedStudentT = {
  studentWithSisMatches: StudentWithSisMatchesT
  districtStudent: DistrictStudentT
  selected: boolean
}

type Props = {
  students: Array<SelectedStudentT>
  onPatchSisIds: (studentSisIds: Array<PatchStudentSisIdT>) => void
}

export default function SisMatchesDisplayContainer(props: Props): React.ReactElement {
  const {students, onPatchSisIds} = props

  const [matchAllChecked, setMatchAllChecked] = React.useState<boolean>(false)
  const [matchNameTeacherChecked, setMatchNameTeacherChecked] = React.useState<boolean>(false)
  const [matchNameGradeChecked, setMatchNameGradeChecked] = React.useState<boolean>(false)
  const [selectedStudents, setSelectedStudents] = React.useState<Array<SelectedStudentT>>(students)

  const selectStudentRow = (student: SelectedStudentT): React.ReactElement => {
    const shouldBeChecked = student !== null && student !== undefined ? student.selected : false
    return (
      <input
        type="checkbox"
        checked={shouldBeChecked}
        onChange={() => onChangeStudentRowCheckbox(student)}
      />
    )
  }

  const onChangeStudentRowCheckbox = (student: SelectedStudentT) => {
    const updatedStudent = {
      studentWithSisMatches: student.studentWithSisMatches,
      districtStudent: student.districtStudent,
      selected: !student.selected
    }

    setSelectedStudents(prevSelectedStudents =>
      map(prevSelectedStudents, selectedStudent => {
        return selectedStudent === student ? updatedStudent : selectedStudent
      })
    )
  }

  const districtStudentMatchAccessor = (student: SelectedStudentT): React.ReactElement => {
    const districtStudentArray = map(student.studentWithSisMatches.sisMatches, districtStudent => {
      const matchDetails = join(
        filter(
          maybe(
            () => [teacherNamesAttr(districtStudent.teachers)],
            grade => [`${grade}`, teacherNamesAttr(districtStudent.teachers)],
            districtStudent.schoolGrade
          ),
          x => !!x
        ),
        ', '
      )
      const matchDetailsDisplay = matchDetails.length > 0 ? `(${matchDetails})` : ''
      return (
        <option key={districtStudent.sisId} value={districtStudent.sisId}>
          {districtStudent.firstName} {districtStudent.lastName} {matchDetailsDisplay}
        </option>
      )
    })

    return (
      <select
        id="district-student-match"
        onChange={event => onChangeStudentDropdown(event, student)}
        value={fromMaybe(() => '', student.districtStudent.sisId)}
      >
        {districtStudentArray}
      </select>
    )
  }

  const onChangeStudentDropdown = (
    e: React.ChangeEvent<HTMLSelectElement>,
    student: SelectedStudentT
  ) => {
    const updatedDistrictStudentMatch = student.studentWithSisMatches.sisMatches.find(sisMatch => {
      return sisMatch.sisId === e.target.value
    })

    if (updatedDistrictStudentMatch !== null && updatedDistrictStudentMatch !== undefined) {
      const updatedStudent = {
        studentWithSisMatches: student.studentWithSisMatches,
        districtStudent: updatedDistrictStudentMatch,
        selected: student.selected
      }

      setSelectedStudents(prevSelectedStudents =>
        map(prevSelectedStudents, selectedStudent => {
          return selectedStudent === student ? updatedStudent : selectedStudent
        })
      )
    }
  }

  const onChangeAllMatchCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = !!event.target.checked
    setSelectedStudents(prevSelectedStudents =>
      onChangeMatchCheckbox(prevSelectedStudents, isChecked, 'all-match')
    )
    setMatchAllChecked(isChecked)
  }

  const onChangeNameTeacherCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = !!event.target.checked
    setSelectedStudents(prevSelectedStudents =>
      onChangeMatchCheckbox(prevSelectedStudents, isChecked, 'name-teacher-match')
    )
    setMatchNameTeacherChecked(isChecked)
  }

  const onChangeNameGradeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = !!event.target.checked
    setSelectedStudents(prevSelectedStudents =>
      onChangeMatchCheckbox(prevSelectedStudents, isChecked, 'name-grade-match')
    )
    setMatchNameGradeChecked(isChecked)
  }

  const onMatchSelectedStudents = () => {
    const studentSisIds = map(
      filter(selectedStudents, selectedStudent => {
        return selectedStudent.selected
      }),
      selectedStudent => {
        return {
          tag: 'PatchStudentSisId' as const,
          studentId: selectedStudent.studentWithSisMatches.id,
          studentSisId: selectedStudent.districtStudent.sisId
        }
      }
    )
    onPatchSisIds(studentSisIds)
  }

  return (
    <SisMatchesDisplay
      matchAllChecked={matchAllChecked}
      matchNameTeacherChecked={matchNameTeacherChecked}
      matchNameGradeChecked={matchNameGradeChecked}
      selectedStudents={selectedStudents}
      selectStudentRow={selectStudentRow}
      districtStudentMatchAccessor={districtStudentMatchAccessor}
      onChangeAllMatchCheckbox={onChangeAllMatchCheckbox}
      onChangeNameTeacherCheckbox={onChangeNameTeacherCheckbox}
      onChangeNameGradeCheckbox={onChangeNameGradeCheckbox}
      onMatchSelectedStudents={onMatchSelectedStudents}
    />
  )
}

/* Function to append teacher Names*/
export function teacherNamesAttr(teachersAttr: Array<TeacherAttrT>): string {
  const teacherNamesArray = map(teachersAttr, teacher => {
    return `${teacher.firstName} ${teacher.lastName}\n\n`
  })
  return teacherNamesArray.join(', ')
}

/* Function to update selectedStudents upon checking group-match checkboxes*/
function checkMatch(selectedStudent: SelectedStudentT, checkType: MatchCheckboxT): boolean {
  const matchesCat = selectedStudent.districtStudent.matches
  switch (checkType) {
    case 'all-match':
      return isAllCategoryMatched(matchesCat)
    case 'name-teacher-match':
      return isNameTeacherMatched(matchesCat)
    case 'name-grade-match':
      return isNameGradeMatched(matchesCat)
    default:
      throw new Error(`Unkown Checkbox Type${checkType}`)
  }
}

function onChangeMatchCheckbox(
  selectedStudents: Array<SelectedStudentT>,
  isChecked: boolean,
  checkType: MatchCheckboxT
): Array<SelectedStudentT> {
  return map(selectedStudents, selectedStudent => {
    const shouldBeChecked = checkMatch(selectedStudent, checkType)
    const updatedStudent = {
      studentWithSisMatches: selectedStudent.studentWithSisMatches,
      districtStudent: selectedStudent.districtStudent,
      selected: isChecked ? shouldBeChecked : !shouldBeChecked
    }
    if (isChecked) {
      /* Selecte every unselected student that matches required category*/
      return selectedStudent.selected ? selectedStudent : updatedStudent
    } else {
      /* Uncheck every selected students that matches required category*/
      return selectedStudent.selected ? updatedStudent : selectedStudent
    }
  })
}

/* Function for identifying student match confidence level */

export function isAllCategoryMatched(matchedCat: Array<string>): boolean {
  return matchedCat.length === 3
}

export function isNameTeacherMatched(matchedCat: Array<string>): boolean {
  const nameTeacherMatch = [MatchCategories.name, MatchCategories.teacher]
  return (
    matchedCat.length === 2 &&
    nameTeacherMatch.includes(matchedCat[0]) &&
    nameTeacherMatch.includes(matchedCat[1])
  )
}
function isNameGradeMatched(matchedCat: Array<string>): boolean {
  const nameGradeMatch = [MatchCategories.name, MatchCategories.grade]
  return (
    matchedCat.length === 2 &&
    nameGradeMatch.includes(matchedCat[0]) &&
    nameGradeMatch.includes(matchedCat[1])
  )
}
